import React, { useSyncExternalStore } from "react";
import { usePageContext } from "vike-react/usePageContext";
import { supportedLocales } from '../i18n/supported-locales';
import { localizeRoute } from '../utils/constants/url-constants';
import { resolvePageLocale } from '../utils/routing/resolve-page-locale';
import { useNavigation } from '../contexts/NavigationContext';
import { navigate } from 'vike/client/router';
import { consumeHistoryPlaceholderFor } from '../utils/view-transitions/job-preview-shell';
import { requiresFullPageLoad } from '../utils/routing/is-static-destination';

const noopSubscribe = () => () => {};

/** False on the server and while hydrating, true afterwards: lets markup that only the client can know wait until hydration is done. */
function useHydrated(): boolean {
  return useSyncExternalStore(
    noopSubscribe,
    () => true,
    () => false,
  );
}

export function Link({ 
  href, 
  children, 
  className, 
  onClick,
  title,
  style,
  onMouseEnter,
  onMouseLeave,
  locale,
  ...props
}: { 
  href: string; 
  children: React.ReactNode; 
  className?: string; 
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  title?: string;
  style?: React.CSSProperties;
  onMouseEnter?: React.MouseEventHandler<HTMLAnchorElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLAnchorElement>;
  locale?: string;
  [key: string]: any;
}) {
  // Ensure pageContext is available - should always work in Vike SSR
  // Use only pageContext (no window fallback) to avoid hydration mismatch: server and client must compute isActive identically
  const pageContext = usePageContext();
  const urlPathname = pageContext?.urlPathname ?? pageContext?.urlParsed?.pathname ?? '';
  
  // Try to get navigation context, but don't fail if it's not available
  let navigation: { startNavigation: () => void; completeNavigation: () => void } | null = null;
  try {
    const navContext = useNavigation();
    navigation = navContext;
  } catch {
    // Navigation context not available, continue without it
  }
  
  // Prefer explicit locale; else resolve from URL + context (pageContext.locale alone can be "en" on /sv/*)
  const currentLocale =
    locale ??
    resolvePageLocale({
      locale: pageContext?.locale,
      urlPathname: pageContext?.urlPathname ?? pageContext?.urlParsed?.pathname,
    });
  
  // Normalize home route: '', '/', or just a locale (e.g., '/en', '/fi', '/sv')
  const isHomeRoute =
    href === '' ||
    href === '/' ||
    supportedLocales.some(locale => href === `/${locale}`);

  let localizedHref = href;

  if (isHomeRoute) {
    // Always emit the locale-prefixed path, including for English — infra (CloudFront)
    // 301s bare "/" to "/en" as the canonical URL, so linking to "/" directly would
    // send every English home link through an avoidable redirect.
    const homeLocale = supportedLocales.includes(currentLocale) ? currentLocale : 'en';
    localizedHref = `/${homeLocale}`;
  } else {
    // Check if href already contains a locale prefix
    const hasLocalePrefix = supportedLocales.some(locale => 
      href.startsWith(`/${locale}/`) || href === `/${locale}`
    );
    // Check if this is a search URL with query parameters (should not be localized)
    const isSearchUrl = href.includes('?') && (
      href.includes('en_search') ||
      href.includes('fi_hae') ||
      href.includes('sv_sok') ||
      href.includes('[page]') ||
      href.includes('[query]')
    );
    // If href already has a locale prefix, keep it — except legacy /{locale}/tools/... where "tools" stayed English
    if (hasLocalePrefix) {
      const legacyEnglishToolsUnderLocale = href.match(/^\/(en|fi|sv)(\/tools\/.*)$/);
      if (legacyEnglishToolsUnderLocale) {
        const loc = legacyEnglishToolsUnderLocale[1] as 'en' | 'fi' | 'sv';
        const englishToolsPath = legacyEnglishToolsUnderLocale[2];
        const localizedPath = localizeRoute(englishToolsPath, loc);
        localizedHref = `/${loc}${localizedPath}`;
      } else {
        localizedHref = href;
      }
    } else if (!isSearchUrl) {
      // Only localize static routes that don't have locale prefix and aren't search URLs
      // Localize the route path for the target locale
      const localizedPath = localizeRoute(href, currentLocale as 'en' | 'fi' | 'sv');
      // Always add locale prefix for consistency across all locales
      localizedHref = `/${currentLocale}${localizedPath}`;
    }
  }
  
  // Remove trailing slash unless it's the root path
  if (localizedHref.length > 1 && localizedHref.endsWith('/')) {
    localizedHref = localizedHref.replace(/\/+$/, '');
  }

  // The server render and the first client render disagreed about "is-active" (the footer's Home link
  // had it on the client only), which broke hydration on every page with a Link in the footer. Only
  // add it once hydrated, so both renders match. No stylesheet targets the class.
  const hydrated = useHydrated();
  const isActive = hydrated && urlPathname.startsWith(localizedHref);
  const combinedClassName = [className, isActive ? "is-active" : null].filter(Boolean).join(" ");

  const handleClick = async (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (typeof window === 'undefined') {
      if (onClick) onClick(event);
      return;
    }

    const targetHref = (event.currentTarget as HTMLAnchorElement).href;
    const currentHref = window.location.href;
    const isExternal = targetHref.startsWith('http') && !targetHref.startsWith(window.location.origin);
    const isAnchor = targetHref.includes('#') && targetHref.split('#')[0] === currentHref.split('#')[0];
    const isSamePage = targetHref === currentHref;
    
    // Call onClick handler first (before navigation)
    // This allows handlers like clearSearch to prevent default and handle navigation themselves
    if (onClick) {
      onClick(event);
    }
    
    // If onClick prevented default, skip navigation
    if (event.defaultPrevented) {
      return;
    }

    // Cmd/Ctrl/Shift/Alt-click (new tab, new window, download) or a non-primary button, and links
    // that open elsewhere (target other than _self) or download: leave it to the browser. Same
    // check as Vike's own link handler (isNormalLeftClick); without it every such click was
    // soft-navigated in the current tab.
    const anchor = event.currentTarget as HTMLAnchorElement;
    const opensElsewhere = (anchor.target && anchor.target !== '_self') || anchor.hasAttribute('download');
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || opensElsewhere) {
      return;
    }

    // Start navigation progress immediately for any navigation
    if (navigation && !isSamePage && !isAnchor) {
      navigation.startNavigation();
    }
    
    // Only intercept same-origin, non-anchor, non-external links to use Vike's router
    if (!isExternal && !isAnchor && !isSamePage) {
      event.preventDefault();

      // Static pages (landing page, resource pages) use injectScriptsAt:null — no Vike
      // bundle is loaded on them. Sending Vike's soft navigate() to these routes calls
      // their onRenderClient, but the DOM never updates cleanly. Hard-navigate instead so
      // the browser makes a fresh HTTP request and gets the server-rendered HTML.
      if (requiresFullPageLoad(localizedHref)) {
        window.location.assign(localizedHref);
        return;
      }

      // Use Vike's navigate function for better integration with page transitions
      try {
        // A job card click may have pushed a same-URL history placeholder for the skeleton job
        // page (job-preview-shell.ts); the job page then overwrites it instead of adding an entry.
        await navigate(
          localizedHref,
          consumeHistoryPlaceholderFor(localizedHref) ? { overwriteLastHistoryEntry: true } : undefined,
        );
      } catch (error) {
        // If navigate fails, fall back to regular navigation
        console.warn('Vike navigate failed, falling back to regular navigation:', error);
        window.location.href = localizedHref;
      }
    } else if (isExternal || isAnchor) {
      // For external links or anchors, let default behavior happen but show progress
      // Progress will be completed by onPageTransitionEnd or timeout
      if (navigation && !isSamePage) {
        setTimeout(() => {
          navigation?.completeNavigation();
        }, 1000);
      }
    }
  };

  return (
    <a 
      href={localizedHref} 
      className={combinedClassName || undefined} 
      onClick={handleClick}
      title={title}
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      {...props}
    >
      {children}
    </a>
  );
}
